import React, { Component } from 'react';
import { Row, Col } from 'antd';
import _EnterpriseOpsService from 'modules/EnterpriseOpsService';
import PageHeader from 'components/enterprise/PageHeader';
import StatCard from 'components/enterprise/StatCard';
import StatusBadge from 'components/enterprise/StatusBadge';
import LoadingSpinner from 'components/enterprise/LoadingSpinner';
import EmptyState from 'components/enterprise/EmptyState';
import { routesAdmin } from 'routes/Routes';

// Sprint 2.4 (Compliance Center): consumes ComplianceDashboardController.GetMetrics (Sprint 2.3),
// which had zero frontend surface before this sprint. Fields shown are exactly what the sprint's
// scope list names — nothing extra invented. StatCard tiles are used throughout rather than a new
// charting-library integration (g2plot/g2/viser-react are installed but have zero existing usage
// anywhere in this codebase to model conventions from — introducing one here was judged higher
// integration risk than the visual benefit justified; see Known Limitations).
class ComplianceCenter extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            metrics: null,
            // Sprint 2.6.3 (Enterprise Operations Console): surfaces SystemHealthController.GetDiagnostics/
            // GetBackupStatus (Sprint 2.3), which had zero frontend callers before this sprint — the
            // Dashboard already links here promising "full System Diagnostics & Backup Status"; this makes
            // that promise true instead of a dead end. No backend change — both endpoints already exist.
            diagnostics: null,
            backupStatus: null
        };
    }

    componentDidMount() {
        this.loadMetrics();
        this.loadSystemHealthDetail();
    }

    loadMetrics = async () => {
        this.setState({ loading: true });
        try {
            const response = await _EnterpriseOpsService.getComplianceMetrics();
            this.setState({ metrics: response.data, loading: false });
        } catch (e) {
            this.setState({ loading: false });
        }
    };

    loadSystemHealthDetail = async () => {
        try {
            const [diagnosticsResponse, backupResponse] = await Promise.all([
                _EnterpriseOpsService.getDiagnostics().catch(() => null),
                _EnterpriseOpsService.getBackupStatus().catch(() => null)
            ]);
            this.setState({
                diagnostics: diagnosticsResponse ? diagnosticsResponse.data : null,
                backupStatus: backupResponse ? backupResponse.data : null
            });
        } catch (e) { }
    };

    render() {
        const { loading, metrics } = this.state;

        return (
            <div className="ent-admin-page">
                <PageHeader
                    title="Compliance Center"
                    subtitle="Consent, password reset, governance, and audit log statistics, plus a recent security-incident summary."
                    breadcrumbItems={[{ label: 'Dashboard', path: routesAdmin.dashboard.path }, { label: 'Compliance Center' }]}
                />

                {loading || !metrics ? <LoadingSpinner tip="Loading compliance metrics..." /> : (
                    <>
                        <div className="ent-section">
                            <div className="ent-section-title">Password Reset Statistics</div>
                            <Row gutter={[16, 16]}>
                                <Col xs={24} sm={12} md={6}>
                                    <StatCard label="Total Issued" value={metrics.PasswordResetStatistics.TotalIssued} tone="info" />
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <StatCard label="Total Used" value={metrics.PasswordResetStatistics.TotalUsed} tone="success" />
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <StatCard label="Expired, Unused" value={metrics.PasswordResetStatistics.TotalExpiredUnused} tone="warning" />
                                </Col>
                            </Row>
                        </div>

                        <div className="ent-section">
                            <div className="ent-section-title">Consent Statistics</div>
                            {(!metrics.ConsentStatistics || metrics.ConsentStatistics.length === 0) ? (
                                <EmptyState icon="file-protect" title="No consent records yet" />
                            ) : (
                                <table className="ent-table">
                                    <thead>
                                        <tr><th>Consent Type</th><th>Version</th><th>Total</th></tr>
                                    </thead>
                                    <tbody>
                                        {metrics.ConsentStatistics.map((row, idx) => (
                                            <tr key={idx}>
                                                <td>{row.ConsentType}</td>
                                                <td>{row.ConsentVersion}</td>
                                                <td>{row.Total}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <div className="ent-section">
                            <div className="ent-section-title">Governance Activity</div>
                            {(!metrics.GovernanceActivity || metrics.GovernanceActivity.length === 0) ? (
                                <EmptyState icon="audit" title="No governance activity yet" />
                            ) : (
                                <table className="ent-table">
                                    <thead>
                                        <tr><th>Request Type</th><th>Status</th><th>Total</th></tr>
                                    </thead>
                                    <tbody>
                                        {metrics.GovernanceActivity.map((row, idx) => (
                                            <tr key={idx}>
                                                <td>{row.RequestType}</td>
                                                <td>{row.Status}</td>
                                                <td>{row.Total}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <div className="ent-section">
                            <div className="ent-section-title">Audit Log Statistics</div>
                            {(!metrics.AuditLogStatistics || metrics.AuditLogStatistics.length === 0) ? (
                                <EmptyState icon="bar-chart" title="No audit events yet" />
                            ) : (
                                <table className="ent-table">
                                    <thead>
                                        <tr><th>Event Type</th><th>Total</th></tr>
                                    </thead>
                                    <tbody>
                                        {metrics.AuditLogStatistics.map((row, idx) => (
                                            <tr key={idx}>
                                                <td>{row.EventType}</td>
                                                <td>{row.Total}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <div className="ent-section">
                            <div className="ent-section-title">Security Incident Summary</div>
                            <Row gutter={[16, 16]} className="mb-3">
                                {metrics.SecurityIncidentSummary && metrics.SecurityIncidentSummary.KeywordCounts &&
                                    Object.keys(metrics.SecurityIncidentSummary.KeywordCounts).map(key => (
                                        <Col xs={24} sm={12} md={6} key={key}>
                                            <StatCard label={key} value={metrics.SecurityIncidentSummary.KeywordCounts[key]} tone={metrics.SecurityIncidentSummary.KeywordCounts[key] > 0 ? 'warning' : 'success'} />
                                        </Col>
                                    ))
                                }
                            </Row>
                            {metrics.SecurityIncidentSummary && metrics.SecurityIncidentSummary.RecentLines && metrics.SecurityIncidentSummary.RecentLines.length > 0 && (
                                <pre style={{ background: 'var(--admin-bg-over-color)', padding: 12, borderRadius: 'var(--admin-border-radius)', maxHeight: 300, overflow: 'auto', fontSize: 12 }}>
                                    {metrics.SecurityIncidentSummary.RecentLines.join('\n')}
                                </pre>
                            )}
                        </div>

                        <div className="ent-section">
                            <div className="ent-section-title">System Diagnostics & Backup Status</div>
                            {!this.state.diagnostics && !this.state.backupStatus ? (
                                <EmptyState icon="tool" title="No diagnostics data available" />
                            ) : (
                                <>
                                    {this.state.backupStatus && (
                                        <Row gutter={[16, 16]} className="mb-3">
                                            <Col xs={24} sm={12} md={6}>
                                                <StatCard label="Backup Status" value={this.state.backupStatus.BackupStatus} tone={this.state.backupStatus.ReminderTriggered ? 'warning' : 'success'} />
                                            </Col>
                                            <Col xs={24} sm={12} md={6}>
                                                <StatCard label="Last Backup Date" value={this.state.backupStatus.LastBackupDate || 'Not recorded'} tone="info" />
                                            </Col>
                                            <Col xs={24} sm={12} md={6}>
                                                <StatCard label="Backup Configured" value={this.state.backupStatus.BackupConfigured ? 'Yes' : 'No'} tone={this.state.backupStatus.BackupConfigured ? 'success' : 'warning'} />
                                            </Col>
                                            {this.state.backupStatus.ReminderTriggered && (
                                                <Col xs={24}>
                                                    <div className="ent-otp-error">{this.state.backupStatus.ReminderReason}</div>
                                                </Col>
                                            )}
                                        </Row>
                                    )}
                                    {this.state.diagnostics && this.state.diagnostics.length > 0 && (
                                        <table className="ent-table">
                                            <thead>
                                                <tr><th>Item</th><th>Category</th><th>Configured</th><th>Severity</th></tr>
                                            </thead>
                                            <tbody>
                                                {this.state.diagnostics.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td>{item.Item}</td>
                                                        <td>{item.Category}</td>
                                                        <td><StatusBadge status={item.IsConfigured ? 'Healthy' : 'NotConfigured'} /></td>
                                                        <td><StatusBadge status={item.Severity} /></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>
        );
    }
}

export default ComplianceCenter;
